import { morph } from 'utils/morph'
import { DialogComponent, DialogCloseEvent } from 'component/dialog'
import { toggleCart, reRenderShopifySections } from 'utils/shopify'
import { addToCart } from 'utils/requests'
import { getIOSVersion, mediaQueryLarge, debounce, getChildSlot } from 'utils/general'
import { parseAttribute } from 'utils/gw'
import { t } from 'utils/shopify'
// STL: tracking helpers (Shop the Look tracking plan, Phase 2)
import {
  trackStlImpression,
  openStlModalContext,
  clearStlModalContext,
  recordStlClickThrough,
  getStlAttributionForQuickAdd,
  trackStlAddToCart,
} from 'utils/stl'
// import { HHVariantPicker } from 'component/variant-picker';

import { LitElement, html, css, nothing } from 'vendor/lit'

export class ShopTheLookComponent extends HTMLElement {
  #abortController = null;
  #cachedContent = new Map();
  #cartUpdateAbortController = new AbortController();
  #currentProductId = null;
  #impressionObserver = null; // STL
  /** @type {ShopTheLookDialog | null} */
  #modal = null;
  /** @type {ShopTheLookThumbnails | null} */
  #thumbnails = null;
  #outfitProductThumbnailData = null;
  #productsThumbnailData = new Map();
  cards = this.querySelectorAll('stl-card');
  cardOptionsOffset = null;
  // featuredImage = JSON.parse(this.getAttribute('image'));
  outfitImage = JSON.parse(this.getAttribute('outfit-image'));
  outfitProduct = parseAttribute(this.getAttribute('outfit-product'));
  products = parseAttribute(this.getAttribute('products'));
  variantImages = JSON.parse(this.getAttribute('product-variant-images'));

  constructor() {
    super();
    this.debouncedResize = debounce(this.handleResize.bind(this), 100);

    // TODO: Change trigger
    // window.addEventListener('load', this.updateThumbnails.bind(this));
    this.updateProductData();
  }

  connectedCallback() {
    this.addEventListener('shop-the-look:view-product', this.#viewProduct.bind(this));
    mediaQueryLarge.addEventListener('change', this.#closeModal);
    document.addEventListener('CART_UPDATED', this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    window.addEventListener('resize', this.debouncedResize)

    this.prefetchOnIdle()
    this.adjustCardContentHeight()

    // STL: section impression + modal link click-throughs
    this.#observeImpression()
    document.addEventListener('click', this.#onModalLinkClick, {
      capture: true,
      signal: this.#cartUpdateAbortController.signal,
    })
  }

  disconnectedCallback() {
    this.removeEventListener('shop-the-look:view-product', this.#viewProduct.bind(this));
    mediaQueryLarge.removeEventListener('change', this.#closeModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    window.removeEventListener('resize', this.debouncedResize);
    this.#impressionObserver?.disconnect(); // STL
  }

  // STL: fires stl_impression once, when half of the first card is visible
  #observeImpression() {
    const firstCard = this.querySelector('stl-card')
    if (!firstCard || !('IntersectionObserver' in window)) return

    this.#impressionObserver = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return
      this.#impressionObserver.disconnect()
      trackStlImpression(this)
    }, { threshold: 0.5 })

    this.#impressionObserver.observe(firstCard)
  }

  // STL: remembers a modal -> product page click-through for 30 minutes
  #onModalLinkClick = (event) => {
    const link = event.target.closest('#stl-dialog a.product__full-details, #stl-dialog #stl-modal-title a')
    if (link) recordStlClickThrough(link)
  }

  prefetchOnIdle() {
    if (!this.products?.length) return

    requestIdleCallback(async () => {
      for (const product of this.products) {
        if (this.getCachedContent(product.url)) continue;
        await this.cacheContent(product.url);
      }
    })
  }

  async updateProductData() {
    // Await registration if stl-card exists but is not yet registered
    const productCards = Array.from(this.querySelectorAll('stl-card'));

    if (productCards.length > 0) {
      if (!customElements.get('stl-card')) {
        await customElements.whenDefined('stl-card');
      }


      const products = this.products.map(product => {
        const productCard = productCards.find(card => card.productId === product.id);
        return {
          ...product,
          url: productCard?.productUrl ?? `${window.location.origin}/products/${product.handle}`,
        };
      });


      this.products = products
    }

    if (!this.products) return

    if (!customElements.get('stl-thumbnails')) {
      await customElements.whenDefined('stl-thumbnails');
    }

    this.updateThumbnails(true);
  }

  set currentProduct(value) {
    this.#currentProductId = parseInt(value, 10);
  }

  get currentProduct() {
    return this.#currentProductId;
  }

  get modal() {
    this.#modal ??= document.getElementById('stl-dialog');
    return this.#modal;
  }

  set modal(value) {
    this.#modal = value;
  }

  get thumbnails() {
    this.#thumbnails ??= this.modal.querySelector('stl-thumbnails');
    return this.#thumbnails;
  }

  set thumbnails(value) {
    this.#thumbnails = value;
  }

  async cacheContent(url) {
    let productWrapper = null

    // Fetch and cache the content
    const html = await this.fetchProductPage(url);
    if (html) {
      const productWrapperElement = html.querySelector('.product--wrapper');
      if (productWrapperElement) {
        // Cache the cloned element to avoid modifying the original
        productWrapper = (productWrapperElement.cloneNode(true));
        const sizeGuideModal = productWrapper.querySelector('.size-calculator--modal');
        if (sizeGuideModal) {
          const sizeGuideModalTrigger = productWrapper.querySelector(`hh-broadcast[aria-controls="${sizeGuideModal.id}"]`);
          const initialSizeGuideModalId = sizeGuideModal.id;
          const sizeGuideModalId = initialSizeGuideModalId + '-stl';
          sizeGuideModal.setAttribute('id', sizeGuideModalId);
          sizeGuideModalTrigger.setAttribute('aria-controls', sizeGuideModalId);
          sizeGuideModalTrigger.setAttribute('data', sizeGuideModalTrigger.getAttribute('data').replace(initialSizeGuideModalId, sizeGuideModalId));
          productWrapper.firstElementChild.appendChild(sizeGuideModal)
        }
        this.updateCachedContent(url, productWrapper);
        sessionStorage.setItem(`stl-${url}`, productWrapper.outerHTML);
      }
    }

    return productWrapper;
  }

  #viewProduct = async (event) => {
    const { id, url, trigger = 'card' } = event.detail; // STL: trigger added

    this.currentProduct = id;

    let productWrapper = this.getCachedContent(url);

    if (!productWrapper) {
      // Fetch and cache the content
      const html = await this.fetchProductPage(url);
      if (html) {
        const productWrapperElement = html.querySelector('.product--wrapper');
        if (productWrapperElement) {
          productWrapper = await this.cacheContent(url);
        }
      }
    }

    if (productWrapper) {
      // Use a fresh clone from the cache
      let freshContent = /** @type {Element} */ (productWrapper.cloneNode(true));

      // Link the product title to its product page (STL modal only, not the PDP)
      const titleEl = freshContent.querySelector('.product--title');
      if (titleEl && !titleEl.querySelector('a')) {
        const titleLink = document.createElement('a');
        titleLink.href = url;
        titleLink.textContent = titleEl.textContent.trim();
        titleEl.textContent = '';
        titleEl.appendChild(titleLink);
      }

      const fullDetailsLink = document.createElement('a');
      fullDetailsLink.classList.add('product__full-details');
      fullDetailsLink.href = url;
      fullDetailsLink.textContent = t('products.product.full_details');
      freshContent.querySelector('.product__info-container').appendChild(fullDetailsLink);

      freshContent = await this.updateGalleryStatus(freshContent);

      await this.updateModal(freshContent);
      // this.#updateVariantPicker(productWrapper);
    }

    this.#openModal();

    // STL: remember which look/product the modal shows; cleared when the dialog closes
    openStlModalContext(this, { productId: id, trigger });
    this.modal.addEventListener(DialogCloseEvent.eventName, clearStlModalContext, { once: true });
  };

  async updateGalleryStatus(element) {
    const parentProductMedia = this.closest('product-wrapper')?.querySelector('hh-product-thumbnails');
    const stlProductMedia = element.querySelector('hh-product-thumbnails');

    if (!stlProductMedia) {
      console.debug('[INTL] PDP: STL media gallery not found, retrying...')
      await new Promise(resolve => setTimeout(resolve, 100))
      return await this.updateGalleryStatus(element)
    }

    stlProductMedia?.classList.add('product-media-gallery--stl')
    const regionalMediaEls = stlProductMedia.querySelectorAll('[data-regional-media]');
    const hasInternationalMedia = parentProductMedia?.getAttribute('international-media') === 'true';
    stlProductMedia.setAttribute('international-media', hasInternationalMedia ? 'true' : 'false');
    if (hasInternationalMedia) {
      regionalMediaEls.forEach(el => el.removeAttribute('aria-hidden'));
    } else {
      regionalMediaEls.forEach(el => el.remove());
    }
    stlProductMedia.setAttribute('data-media-status', 'loaded');
    stlProductMedia.classList.remove('is-loading')

    // The modal's option inputs carry form='product-form-<section>', the same ID as the PDP form, so
    // they join the PDP's radio groups and clear its size. Detach them before the content is inserted.
    element.querySelectorAll('hh-variant-picker input[form]').forEach(input => input.removeAttribute('form'));

    return element
  }

  #resetScroll() {
    if (!this.modal) return;

    const modalContent = this.modal.querySelector('.stl-modal__content');
    const productDetails = this.modal.querySelector('.product__info-wrapper');
    // const productMedia = this.modal.querySelector('.product-information__media');
    modalContent?.scrollTo({ top: 0, behavior: 'instant' });
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    // productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }

  #openModal = () => {
    // this.#stayVisibleUntilDialogCloses(this.modal);

    this.modal.showDialog();

    // TODO: Maybe we remove this
    // is nondeterministic when the open attribute is set on the dialog element after .showDialog() is called.
    // Waiting until the open animation starts seemed to be the most reliable metric here.
    const dialog = this.modal.elements?.dialog;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  // closeDialog lives on the <stl-dialog> component, not on its inner <dialog>, and is a no-op when it's closed
  #closeModal = () => {
    this.modal?.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productUrl) {
    if (!productUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const sectionId = document.querySelector('.product--wrapper product-wrapper')?.getAttribute('section-id');
      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');
      html.querySelector('hh-product-thumbnails')?.classList.add('product-media-gallery--stl')
      html.querySelector('product-wrapper')?.setAttribute('section-id', sectionId);
      html.querySelector('shop-the-look')?.remove();
      html.querySelector('.gallery-modal')?.remove();
      html.querySelector('[data-id="international-media-script"]')?.remove();

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  get productData() {
    return {
      source: {
        ...this.outfitProduct,
        featuredImage: this.outfitImage,
      },
      products: this.products,
      currentProduct: this.currentProduct,
    }
  }

  get productCards() {
    return [...this.querySelectorAll('stl-card')];
  }

  updateThumbnails(invalidate = false) {
    const dialogComponent = document.getElementById('stl-dialog');
    const productData = this.productData;
    if (!dialogComponent) return;

    const detail = {
      eventSource: this,
      currentProduct: this.currentProduct,
      productData,
      variantImages: this.variantImages,
    }

    if (invalidate) {
      this.#productsThumbnailData.clear();
      productData.products.forEach(product => {
        this.#productsThumbnailData.set(product.id, this.generateThumbnailData(product));
      });

      this.#outfitProductThumbnailData = this.generateThumbnailData(productData.source);

      detail.thumbnailData = {
        source: this.#outfitProductThumbnailData,
        products: [...this.#productsThumbnailData.values()],
      }
    }

    this.thumbnails.dispatchEvent(new CustomEvent('shop-the-look:update-thumbnails', {
      detail,
    }));
  }

  generateThumbnailData(product) {
    return {
      id: product.id,
      alt: product.featuredImage.alt ?? product.title,
      // url: `${window.location.origin}/products/${product.handle}`,
      url: product.url,
      image: product.featuredImage,
      isSelected: product.id === this.currentProduct,
    }
  }

  updateCachedContent(url, content) {
    this.#cachedContent.set(url, content);
  }

  getCachedContent(url) {
    const sessionStorageContent = sessionStorage.getItem(`stl-${url}`);

    if (sessionStorageContent) {
      const html = new DOMParser().parseFromString(sessionStorageContent, 'text/html')
      return html.querySelector('.product--wrapper');
    }

    return this.#cachedContent.get(url);
  }

  async updateModal(productWrapper) {
    const modal = document.getElementById('stl-dialog');
    const modalContent = document.getElementById('stl-modal-content');

    // TODO: Optimize this to not push so much data?
    this.updateThumbnails();

    // modal.featuredImage = this.parent?.featuredImage || null;
    // modal.products = this.parent?.products || [];

    if (!productWrapper || !modalContent) return;

    this.querySelectorAll('hh-modal').forEach(modal => {
      modal.close();
    });

    morph(modalContent, productWrapper);

    const productTitle = modalContent.querySelector('h1.product--title');
    if (productTitle) {
      const modalTitle = document.createElement('h2');
      for (const attribute of productTitle.attributes) {
        modalTitle.setAttribute(attribute.name, attribute.value);
      }
      modalTitle.id = 'stl-modal-title';
      modalTitle.append(...productTitle.childNodes);
      productTitle.replaceWith(modalTitle);
    }

    modal.dispatchEvent(new CustomEvent('shop-the-look:modal-update'));
  }

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  // get products() {
  //   return Array.from(this.cards).map((card) => card.product);
  // }

  handleResize = () => {
    this.adjustCardContentHeight();
  }

  adjustCardContentHeight() {
    const productCardContentWrappers = this.productCards.map(card => card.querySelector('.card-content__main'));
    for (const wrapper of productCardContentWrappers) {
      wrapper.closest('.simple-card').style.setProperty('--content-height-initial', '');
      wrapper.closest('.simple-card').style.setProperty('--content-height', '');
    }
    const maxContentHeight = Math.max(...productCardContentWrappers.map(wrapper => wrapper.offsetHeight));
    // this.style.height = `${maxContentHeight}px`;
    for (const wrapper of productCardContentWrappers) {
      wrapper.closest('.simple-card').style.setProperty('--content-height-initial', `${wrapper.offsetHeight}px`);
      wrapper.closest('.simple-card').style.setProperty('--content-height', `${maxContentHeight}px`);
    }
  }
}

customElements.define('shop-the-look', ShopTheLookComponent)

export class ShopTheLookCard extends HTMLElement {
  #productId
  #productUrl
  #productImage

  constructor() {
    super();

    this.#productId = parseInt(this.getAttribute('product-id'), 10)
    this.#productUrl = this.getAttribute('product-url')
    this.#productImage = JSON.parse(this.getAttribute('product-image'))
    this.cardOptions = this.querySelector('.swatches')
    this.statusWrapper = this.querySelector('.card__status')
    this.statusEls = {
      quickView:
        this.statusWrapper?.querySelector('.message__quick-view') || null,
      preOrder:
        this.statusWrapper?.querySelector('.message__pre-order') || null,
      added:
        this.statusWrapper?.querySelector('.message__quick-view-added') || null,
      loader: this.statusWrapper?.querySelector('.svg-loader') || null,
    }
    this.isLoading = false
  }

  get parent() {
    return this.closest('shop-the-look')
  }

  get product() {
    return {
      id: this.productId,
      image: this.productImage,
      url: this.productUrl,
    }
  }

  get productUrl() {
    return this.#productUrl;
  }

  set productUrl(value) {
    this.setAttribute('product-url', value);
    this.#productUrl = value;
  }

  get productImage() {
    return this.#productImage;
  }

  set productImage(value) {
    this.setAttribute('product-image', value);
    this.#productImage = value;
  }

  get productId() {
    return this.#productId;
  }

  set productId(value) {
    this.setAttribute('product-id', value);
    this.#productId = value;
  }

  connectedCallback() {
    this.addEventListener('click', this.handleClick.bind(this));
    this.addEventListener('keyup', this.handleKeyUp.bind(this));
    this.addEventListener('keydown', this.handleKeyDown.bind(this));
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick.bind(this));
    this.removeEventListener('keyup', this.handleKeyUp.bind(this));
    this.removeEventListener('keydown', this.handleKeyDown.bind(this));
  }

  changeCardStatus(isLoading = false, response) {
    if (isLoading) {
      this.cardOptions.classList.add('is-disabled')
      this.statusWrapper.classList.remove('is-disabled')
      this.statusEls.loader.classList.add('show')

      setTimeout(() => {
        this.classList.add('is-loading')
        this.statusEls.loader.classList.add('running')
      }, 300)
    } else if (!isLoading && response) {
      this.statusEls.loader.classList.remove('running')
      this.statusEls.loader.classList.remove('show')
      this.statusEls.added.classList.add('show')

      setTimeout(() => {
        this.classList.remove('is-loading')
      }, 1000)

      setTimeout(() => {
        this.statusEls.added.classList.remove('show')
        this.statusWrapper.classList.add('is-disabled')
        this.cardOptions.classList.remove('is-disabled')
      }, 3500)
    }
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  // TODO: Replace image gallery with different component
  handleClick = async (e) => {
    e.preventDefault();
    document.activeElement.blur();

    if (e.target.classList.contains('js-swatch')) {
      this.querySelectorAll('.js-swatch').forEach(button => {
        button.setAttribute('aria-pressed', String(button === e.target));
      });
      this.changeCardStatus(true)
      const variantID = e.target.dataset.variantId
      // STL: tag the cart line as a quick add from this look
      const stl = this.parent ? getStlAttributionForQuickAdd(this.parent, e.target.dataset.productId) : null
      const properties = { ...JSON.parse(e.target.dataset.properties), ...(stl?.properties ?? {}) }
      const handle = e.target.dataset.handle
      const payload = { id: variantID, properties }
      const response = await addToCart(payload, 'cart-drawer,header')

      if (response) trackStlAddToCart(stl, payload, response) // STL

      toggleCart(true)

      if (
        response?.sections &&
        typeof response.sections === 'object' &&
        Object.keys(response.sections).length
      ) {
        reRenderShopifySections(response)
      }

      this.changeCardStatus(false, response)

      // Dispatch saved cart item event for customer accounts
      const gwCustomerAccounts = document.querySelector('gw-customer-accounts')
      if (gwCustomerAccounts) {
        const variantId = e.target.dataset?.variantId
        const productId = e.target.dataset?.productId
        if (!variantId || !productId) return

        const event = new CustomEvent('gw:saved-items-from-cart', {
          detail: {
            productId: productId,
            variantId: variantId,
            target: e.target,
            extra: {
              ...response,
              handle: handle
            }
          },
        })

        gwCustomerAccounts.dispatchEvent(event)
      }
    } else if (this.cardOptions && !matchMedia('(hover: hover)').matches && !this.querySelector('.simple-card').classList.contains('options-visible')) {
      this.querySelector('.simple-card').classList.add('options-visible')
    } else {
      e.stopPropagation();
      const { productId: id, productImage: image, productUrl: url } = this;

      this.dispatchEvent(new CustomEvent('shop-the-look:view-product', {
        bubbles: true,
        composed: true,
        detail: { id, image, url, trigger: 'card' }, // STL: trigger added
      }));
    }
  };

  handleKeyUp = async (event) => {
    event.preventDefault();
    if (event.key === 'Enter' || event.key === ' ') {
      this.handleClick(event);
    }
  };

  handleKeyDown = (event) => {
    if (event.key === ' ') {
      event.preventDefault(); // Prevent browser scroll on Space
    }
  };
}

if (!customElements.get('stl-card')) {
  customElements.define('stl-card', ShopTheLookCard);
}

class ShopTheLookDialog extends DialogComponent {
  #abortController = new AbortController();
  #productData = {};
  #products = [];
  #featuredImage = null;
  thumbnails = this.querySelector('stl-thumbnails');

  get productData() {
    return this.#productData;
  }

  set productData(value) {
    this.#productData = value;
    this.onProductsChanged();
  }

  get test() {
    return 'working dialog'
  }

  // TODO: Combine featured image and products update event
  get products() {
    return this.#products;
  }

  get featuredImage() {
    return this.#featuredImage;
  }

  set products(value) {
    this.#products = value;
    this.onProductsChanged();
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener('CART_UPDATED', this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog
   * @param {CustomEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data?.didError) return;
    this.closeDialog();
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };

  generateThumbnailData(product) {
    return {
      id: product.id,
      alt: product.featuredImage.alt ?? product.title,
      // url: `${window.location.origin}/products/${product.handle}`,
      url: `${window.location.origin}/products/${product.handle}`,
      image: product.featuredImage,
    }
  }

  onProductsChanged() {
    console.trace('this.#productData', this.#productData)
    const thumbnailData = {
      source: this.generateThumbnailData(this.#productData.source),
      products: this.#productData.products.map(this.generateThumbnailData.bind(this)),
    }

    this.dispatchEvent(new CustomEvent('products-changed', {
      detail: {
        productData: this.productData,
        thumbnailData
      },
    }));
  }
}

if (!customElements.get('stl-dialog')) {
  customElements.define('stl-dialog', ShopTheLookDialog);
}

class ShopTheLookThumbnails extends LitElement {
  static thumbnailSize = 60;
  static properties = {
    thumbnailData: {
      type: Object,
    },
    sourceProduct: {
      type: Object,
    },
    currentProductId: {
      type: Number,
    },
    eventSource: {
      type: Object,
    },
  };
  parent = this.closest("stl-dialog");

  constructor() {
    super();
    this.addEventListener(
      "shop-the-look:update-thumbnails",
      this.updateThumbnails.bind(this),
    );
  }

  updated() {
    setTimeout(() => {
      this.shadowRoot.querySelectorAll('adaptive-image').forEach(image => {
        image.init();
        image.requestUpdate();
      });
    }, 100)
  }

  handleThumbnailClick = (event) => {
    event.preventDefault();

    const target = event.target.closest('.thumbnail-item');

    if (!target) return;

    const id = target.getAttribute('data-id');
    const url = target.getAttribute('data-url');

    this.eventSource.dispatchEvent(new CustomEvent('shop-the-look:view-product', {
      bubbles: true,
      composed: true,
      detail: {
        id: target.getAttribute('data-id'),
        url: target.getAttribute('data-url'),
        trigger: 'thumbnail', // STL
      },
    }));
  };

  updateThumbnails(e) {
    const { eventSource, currentProduct, thumbnailData, variantImages } = e.detail;
    this.eventSource ??= eventSource;
    this.variantImages ??= variantImages;
    if (thumbnailData) {
      this.thumbnailData = thumbnailData;
    }
    this.currentProductId = currentProduct;
  }

  renderThumbnail(image, size = 60) {
    if (!image) return html`
      <div class="thumbnail-item-placeholder"></div>
    `;

    return html`
      <adaptive-image
        class="thumbnail-item__image"
        data-image="${JSON.stringify(image)}"
        data-alt="${image.alt}"
        data-sizes="${size}px"
        data-aspect-ratio="1"
      ></adaptive-image>
    `;
  }

  renderThumbnailItem(product) {
    return html`
      <button class="thumbnail-item" @click=${this.handleThumbnailClick} data-id="${product.id}" data-url="${product.url}" aria-label="${product.alt}" type="button" aria-current="${product.id === this.currentProductId ? 'true' : nothing}">
        ${this.renderThumbnail(product.image)}
      </button>
    `;
  }

  // TODO: Update sizes attributes
  // TODO: Set active state
  render() {
    const { source, products = [] } = this.thumbnailData ?? {};
    return html`
      <div class="inner">
        <div class="outfit-thumbnail">
          <div class="thumbnail-item thumbnail-item--large" data-id="${source?.id}">
            ${this.renderThumbnail(source?.image, 100)}
          </div>
        </div>
        <div class="product-thumbnails">
          <h2 class="product-thumbnails__title">${t('products.shop_the_look.in_this_look')}</h2>
          <div class="thumbnail-track">
            ${products?.map((product) => this.renderThumbnailItem(product))}
          </div>
        </div>
      </div>
    `;
  }

  static styles = css`
    :host {
      --gap: 1.2rem;
      --thumbnail-size: 60px;
      --thumbnail-size-large: 100px;
    }

    .inner {
      display: flex;
      gap: var(--gap);
    }
    .outfit-thumbnail {
      flex-basis: 100px;
    }
    .product-thumbnails {
      align-self: center;
      flex-grow: 1;
      padding-inline-end: 0.4rem;
    }
    .thumbnail-track {
      display: flex;
      gap: var(--gap);
    }
    .thumbnail-item {
      --size: var(--thumbnail-size);
      appearance: none;
      aspect-ratio: 1 / 1;
      cursor: pointer;
      overflow: hidden;
      display: block;
      border: 0;
      border-radius: 0;
      padding: 0;
      position: relative;
      max-width: var(--size);
      width: 100%;

      &::after {
        content: '';
        position: absolute;
        inset: 0;
        pointer-events: none;
        box-shadow: rgb(0 0 0 / 0.05) 0 0 0 1px inset;
        transition: box-shadow 0.15s ease;
        z-index: 1;
      }

      @media (hover: hover) {
        &:hover {
          &::after {
            box-shadow: rgb(0 0 0 / 0.1) 0 0 0 2px inset;
          }
        }
      }

      &[aria-current="true"] {
        &::after {
          box-shadow: rgb(0 0 0 / 1) 0 0 0 2px inset;
        }
      }
    }
    .thumbnail-item--large {
      --size: var(--thumbnail-size-large);
    }
    .thumbnail-item__image {
      aspect-ratio: 1 / 1;
    }

    h2 {
      color: var(--hh-color-ink);
      font-family: var(--hh-font-gotham-medium);
      font-size: 10px;
      font-weight: 400;
      letter-spacing: 0.02em;
      line-height: 1.2;
      text-transform: uppercase;
      margin: 0 0 1.2rem;
    }

    ::slotted(img) {
      width: var(--image-width, 300px);
      height: var(--image-height, 300px);
      object-fit: cover;
    }
    ::slotted(.hidden),
    ::slotted([data-src]) {
      display: none;
    }
  `;
}

customElements.define('stl-thumbnails', ShopTheLookThumbnails)
